import Compressor from "compressorjs"

let canUseWebpCache: boolean | undefined

export const canUseWebp = (): boolean => {
    if (canUseWebpCache !== undefined) return canUseWebpCache
    try {
        const canvas = document.createElement("canvas")
        canUseWebpCache = canvas
            .toDataURL("image/webp")
            .startsWith("data:image/webp")
    } catch {
        canUseWebpCache = false
    }
    return canUseWebpCache
}

export const imageCompressToWebp = (
    file: Blob,
    options?: Compressor.Options
): Promise<Blob> => {
    const mimeType = canUseWebp() ? "image/webp" : "image/jpeg"
    return imageCompress(file, {
        quality: 0.7,
        maxWidth: 1600,
        maxHeight: 1600,
        mimeType,
        ...(options ?? {})
    })
}

export const stringToBlob = (data: string, mimetype: string): Blob => {
    const bin = atob(data.replace(/^.*,/, ""))
    const buffer = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i)
    }
    return new Blob([buffer.buffer], { type: mimetype })
}

export const imageCompress = (
    file: Blob,
    options?: Compressor.Options
): Promise<Blob> =>
    new Promise((resolve, reject) => {
        new Compressor(file, {
            quality: 0.7,
            maxWidth: 3000,
            maxHeight: 3000,
            ...(options ?? {}),
            success(compressedFile: Blob): void {
                resolve(compressedFile)
            },
            error(error: Error): void {
                console.error("[imageCompress] 圧縮失敗", {
                    originalSize: file.size,
                    originalType: file.type,
                    error,
                    errorMessage: error.message,
                    errorStack: error.stack
                })
                reject(new Error(`画像圧縮に失敗しました: ${error.message}`))
            }
        })
    })

export const blobToString = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
            const result = reader.result as string
            resolve(result)
        }

        reader.onerror = () => {
            const error = new Error(
                `FileReaderでの画像読み込みに失敗しました。` +
                    `Blobサイズ: ${blob.size} bytes, Blobタイプ: ${blob.type}, ` +
                    `FileReaderエラー: ${reader.error?.name || "不明"} - ${reader.error?.message || "メッセージなし"}`
            )
            console.error("[blobToString] FileReaderエラー詳細", {
                blobSize: blob.size,
                blobType: blob.type,
                readerError: reader.error,
                errorName: reader.error?.name,
                errorMessage: reader.error?.message
            })
            reject(error)
        }

        try {
            reader.readAsDataURL(blob)
        } catch (error) {
            console.error("[blobToString] readAsDataURL呼び出し時の例外", {
                error,
                blobSize: blob.size,
                blobType: blob.type
            })
            reject(error instanceof Error ? error : new Error(String(error)))
        }
    })

export const imageToCompressedString = async (
    file: Blob,
    options?: Compressor.Options
): Promise<string> => {
    const compressedImage = await imageCompress(file, options)
    const stringifyImage = await blobToString(compressedImage)
    return stringifyImage
}

/**
 * 画像ファイルがブラウザで読み込み可能かを検証する。
 * Object URL を作成して Image 要素で読み込みテストを行う。
 */
export const validateImageFile = (file: Blob): Promise<boolean> =>
    new Promise(resolve => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
            URL.revokeObjectURL(url)
            resolve(true)
        }
        img.onerror = () => {
            URL.revokeObjectURL(url)
            console.error("[validateImageFile] 画像の読み込みに失敗", {
                size: file.size,
                type: file.type
            })
            resolve(false)
        }
        img.src = url
    })

export const imagesToCompressedStrings = async (
    files: Blob[],
    options?: Compressor.Options
): Promise<string[]> => {
    const stringifyImages: string[] = []

    for (let i = 0; i < files.length; i++) {
        const file = files[i]
        try {
            const stringifyImage = await imageToCompressedString(file, options)
            stringifyImages.push(stringifyImage)
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error)
            console.error(
                `[imagesToCompressedStrings] 画像 ${i + 1}/${files.length} の処理に失敗`,
                {
                    index: i,
                    fileSize: file.size,
                    fileType: file.type,
                    error,
                    errorMessage,
                    errorStack: error instanceof Error ? error.stack : undefined
                }
            )
            throw new Error(
                `画像 ${i + 1}/${files.length} の処理に失敗しました: ${errorMessage}`,
                { cause: error }
            )
        }
    }

    return stringifyImages
}
